import { useState } from 'react';
import { TextArea, TextField } from './FormField';

/**
 * Reusable UI for a SharePoint document-library-backed "resources" panel.
 * Both the Sales page (Sales Resources) and My Account (Team Member
 * Resources) render one of these, so the tile grid + upload form live
 * here instead of being duplicated. Each page wires up the upload /
 * delete handlers itself using the matching service module.
 *
 * Exports:
 *   - <ResourcesGrid>  — read-only tiles, one per resource, with a
 *                        Download link and an optional Remove button.
 *   - <ResourceForm>   — upload modal contents (File + Title + Category
 *                        + Description). Shows any upload error inline
 *                        rather than surfacing it elsewhere.
 */

// Format a byte count as "12 KB" / "3.4 MB" for the tile footer.
function formatFileSize(bytes) {
  if (bytes == null || !Number.isFinite(Number(bytes))) return '';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Tile grid of resources. Caller decides whether the Remove button shows
 * (typically Director-only) via `canManage`.
 *
 * @param {object} props
 * @param {object[]} props.resources      — rows from listLibraryItems
 * @param {boolean}  [props.canManage]    — show Remove button when true
 * @param {function} [props.onRemove]     — called with a row when Remove clicked
 * @param {string}   [props.emptyMessage] — rendered when resources is empty
 */
export function ResourcesGrid({
  resources,
  canManage = false,
  onRemove,
  emptyMessage = 'No resources yet.',
}) {
  if (!resources?.length) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'grid',
        // Target two tiles per row on desktop (~half the content width each)
        // while still collapsing to one column on narrow/tablet screens.
        gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))',
        gap: 16,
      }}
    >
      {resources.map((r) => (
        <ResourceTile
          key={r.id}
          resource={r}
          canManage={canManage}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

function ResourceTile({ resource, canManage, onRemove }) {
  // SharePoint's @microsoft.graph.downloadUrl is a short-lived,
  // pre-authenticated URL. Using it directly on an <a href> lets the
  // browser hit SharePoint without a token — the URL itself is the
  // capability. The `download` attribute asks the browser to save
  // rather than navigate.
  const downloadUrl = resource.downloadUrl;
  const sizeLabel = formatFileSize(resource.sizeBytes);
  const fileLabel = [resource.fileName, sizeLabel].filter(Boolean).join(' · ');

  return (
    <div
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        height: '100%',
      }}
    >
      {resource.Category && (
        <span
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'var(--brand-muted)',
          }}
        >
          {resource.Category}
        </span>
      )}
      <div className="card-title" style={{ marginBottom: 0 }}>
        {resource.Title}
      </div>
      {resource.Description && (
        <p style={{ margin: 0, color: 'var(--brand-ink)' }}>
          {resource.Description}
        </p>
      )}
      {fileLabel && (
        <div style={{ fontSize: 12, color: 'var(--brand-muted)' }}>
          {fileLabel}
        </div>
      )}
      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          paddingTop: 8,
        }}
      >
        {downloadUrl ? (
          <a
            href={downloadUrl}
            download={resource.fileName || true}
            className="btn btn-primary btn-sm"
          >
            Download
          </a>
        ) : (
          <span className="muted" style={{ fontSize: 12 }}>
            File unavailable
          </span>
        )}
        {canManage && onRemove && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => onRemove(resource)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Upload form for a resource. Used inside a Modal on whatever page is
 * rendering the grid. Caller provides the onSave handler (which should
 * call the matching service's `createXxxResource`) and handles its own
 * busy/error state.
 *
 * @param {object} props
 * @param {boolean} props.saving
 * @param {string}  [props.error]               — upload error message to show
 * @param {function} props.onSave               — called with {file, Title, Description, Category}
 * @param {function} props.onCancel
 * @param {string}  [props.categoryPlaceholder] — hint for the Category input
 * @param {string}  [props.submitLabel]         — button label (default "Upload Resource")
 * @param {string}  [props.savingLabel]         — busy label (default "Uploading…")
 */
export function ResourceForm({
  saving,
  error,
  onSave,
  onCancel,
  categoryPlaceholder = 'e.g. One-Pager, Deck, Case Study',
  submitLabel = 'Upload Resource',
  savingLabel = 'Uploading…',
}) {
  const [model, setModel] = useState({
    Title: '',
    Description: '',
    Category: '',
    file: null,
  });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // When a file is picked, auto-fill Title from the base name if the
  // user hasn't already typed one — matches SharePoint's UX where the
  // filename becomes the list item's display title.
  const onFileChange = (file) => {
    setModel((m) => {
      const baseName = (file?.name || '').replace(/\.[^.]+$/, '');
      return {
        ...m,
        file: file || null,
        Title: m.Title || baseName,
      };
    });
    // Clear any stale file error the moment a file is picked.
    setErrors((prev) => {
      if (!prev.file) return prev;
      const { file: _drop, ...rest } = prev;
      return rest;
    });
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!model.Title.trim()) next.Title = 'Title is required.';
    if (!model.file) next.file = 'Pick a file to upload.';
    setErrors(next);
    if (Object.keys(next).length) return;
    onSave(model);
  };

  return (
    <form onSubmit={submit}>
      {/* Show the actual SharePoint/Graph error when an upload fails so
          the user can either fix it themselves or paste it into a
          support request. Collapsed when empty to keep the modal tidy. */}
      {error && (
        <div
          className="card"
          style={{
            borderColor: 'var(--brand-danger)',
            background: 'rgba(220, 38, 38, 0.04)',
            marginBottom: 12,
          }}
          role="alert"
        >
          <div className="card-title" style={{ marginBottom: 4 }}>
            Upload Failed
          </div>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</p>
        </div>
      )}
      <div className="form-grid">
        <div className="field full">
          <label>File</label>
          <input
            type="file"
            onChange={(e) => onFileChange(e.target.files?.[0])}
            disabled={saving}
          />
          {model.file && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--brand-muted)',
                marginTop: 4,
              }}
            >
              {model.file.name}
              {Number.isFinite(model.file.size)
                ? ` · ${(model.file.size / (1024 * 1024)).toFixed(1)} MB`
                : ''}
            </span>
          )}
          {errors.file && <span className="error">{errors.file}</span>}
        </div>
        <TextField
          label="Title"
          value={model.Title}
          onChange={(v) => set('Title', v)}
          error={errors.Title}
          required
          full
        />
        <TextField
          label="Category"
          value={model.Category}
          onChange={(v) => set('Category', v)}
          placeholder={categoryPlaceholder}
          full
        />
        <TextArea
          label="Description (optional)"
          value={model.Description}
          onChange={(v) => set('Description', v)}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 16,
        }}
      >
        <button type="button" className="btn btn-outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? savingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}
